import { zodResolver } from "@hookform/resolvers/zod";
import { proposalImplementationPersonalReferenceFormDataSchema } from "@repo/core/helpers/zod.schemas";
import { ROUTES } from "@repo/core/routes";
import { useProposalFlowNavigation } from "../../utils/proposalFlowNavigation";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useCreditProposal } from "../hooks/useCreditProposal";
import { useEffect, useMemo } from "react";
import { formatCellPhone } from "@repo/core/utils/mask.utils";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import useToast from "@repo/core/components/core/Toast";
import {
  matchRelationshipCode,
  resolveRelationshipDescription
} from "./relationshipMatcher";
import type { ConsultProposalResponseDTO } from "@repo/core/services/entities/Proposals";

const requisitionsResource = new RequisitionsResource(wayupApiInstance);

type ProposalImplementationPersonalReferenceFormDataSchema = z.infer<
  typeof proposalImplementationPersonalReferenceFormDataSchema
>;

type PersonalReferenceProposalAliases = Partial<ConsultProposalResponseDTO> & {
  strEndDdd2?: string;
  strEndFone2?: string;
};

const normalizePhoneDigits = (
  ddd?: string | null,
  phone?: string | null
): string => `${ddd || ""}${phone || ""}`.replace(/\D/g, "");

export const PersonalReferenceFormController = () => {
  const { replace, completeStep, goToPreviousStep } =
    useProposalFlowNavigation();
  const toast = useToast();
  const {
    updateProposalData,
    proposalData,
    resetCreditProposalStore,
    persistFullProposalSnapshot
  } = useCreditProposal();

  const { control, handleSubmit, reset, setError } =
    useForm<ProposalImplementationPersonalReferenceFormDataSchema>({
      resolver: zodResolver(
        proposalImplementationPersonalReferenceFormDataSchema
      ),
      mode: "onSubmit",
      reValidateMode: "onChange"
    });

  const { data: relationshipOptions, isLoading: isLoadingRelationshipOptions } =
    useQuery({
      queryKey: ["personalRelationshipTypes"],
      queryFn: () => requisitionsResource.getPersonalRelationshipTypes(),
      select: (response) =>
        response.data.map((item) => ({
          value: item.codigo,
          label: item.descricao
        }))
    });

  const rawRelationship = proposalData?.strRefPessRel1?.trim() ?? "";

  const relationshipOptionsList = useMemo(() => {
    return relationshipOptions ?? [];
  }, [relationshipOptions]);

  // Load initial values from store when proposal or relationship list is ready
  useEffect(() => {
    if (!proposalData?.strRefPessNome1 && !proposalData?.strRefPessRel1) {
      return;
    }

    const phone =
      proposalData.strRefPessDdd1 && proposalData.strRefPessFone1
        ? formatCellPhone(
            proposalData.strRefPessDdd1,
            proposalData.strRefPessFone1
          )
        : "";

    const relationship = relationshipOptions?.length
      ? matchRelationshipCode(rawRelationship, relationshipOptionsList)
      : "";

    reset({
      name: proposalData.strRefPessNome1 || "",
      relationship,
      cellPhone: phone
    });
  }, [
    proposalData,
    rawRelationship,
    relationshipOptions,
    relationshipOptionsList,
    reset
  ]);

  const handleOnSubmitForm = handleSubmit(async (formData) => {
    if (proposalData) {
      const proposalAliases = proposalData as PersonalReferenceProposalAliases;
      const phoneDigitsOnly = formData.cellPhone?.replace(/\D/g, "") || "";

      const clientDdd =
        proposalData.strCelularDDD || proposalData.strDDDEnvioToken || "";
      const clientPhone =
        proposalData.strCelular || proposalData.strFoneEnvioToken || "";
      const clientPhoneDigits = normalizePhoneDigits(clientDdd, clientPhone);
      const additionalPhoneDigits = normalizePhoneDigits(
        proposalAliases.strEndDdd2 || proposalAliases.strResDDD2,
        proposalAliases.strEndFone2 || proposalAliases.strResFone2
      );

      if (
        clientPhoneDigits.length >= 10 &&
        phoneDigitsOnly.length >= 10 &&
        clientPhoneDigits === phoneDigitsOnly
      ) {
        setError("cellPhone", {
          type: "manual",
          message:
            "O telefone de referência não pode ser o mesmo do cliente da proposta."
        });
        return;
      }

      if (
        additionalPhoneDigits.length >= 10 &&
        phoneDigitsOnly.length >= 10 &&
        additionalPhoneDigits === phoneDigitsOnly
      ) {
        setError("cellPhone", {
          type: "manual",
          message:
            "O telefone de referência não pode ser o mesmo do telefone adicional."
        });
        return;
      }

      let ddd = "";
      let phone = "";

      if (phoneDigitsOnly.length >= 10) {
        ddd = phoneDigitsOnly.substring(0, 2);
        phone = phoneDigitsOnly.substring(2);
      } else if (phoneDigitsOnly.length >= 2) {
        ddd = phoneDigitsOnly.substring(0, 2);
        phone = phoneDigitsOnly.substring(2);
      }

      updateProposalData({
        strRefPessNome1: formData.name,
        strRefPessRel1: resolveRelationshipDescription(
          formData.relationship,
          relationshipOptionsList
        ),
        strRefPessDdd1: ddd,
        strRefPessFone1: phone
      });
    }

    try {
      await persistFullProposalSnapshot();
    } catch {
      toast.error(
        "Erro ao salvar etapa",
        "Não foi possível salvar os dados desta etapa. Tente novamente."
      );
      return;
    }

    completeStep(ROUTES.creditProposalReview);
  });

  const handleGoBack = () => {
    goToPreviousStep(ROUTES.creditProposalInvoiceData);
  };

  const handleClose = () => {
    resetCreditProposalStore();
    replace(ROUTES.home);
  };

  return {
    control,
    handleOnSubmitForm,
    handleGoBack,
    handleClose,
    relationshipOptions: relationshipOptionsList,
    isLoadingRelationshipOptions
  };
};
